import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";
import { Marquee } from "@/components/ui/marquee";

/** Depoimentos fictícios para a landing; textos genéricos sobre atendimento, CRM e agentes de IA. */
const testimonials = [
  {
    quote:
      "O FabChat mudou nosso atendimento de verdade. Em poucas semanas a primeira resposta ficou bem mais rápida e o comercial passou a focar só nos leads que estão quentes. Conectar o WhatsApp foi simples.",
    name: "Marina Oliveira",
    role: "Diretora de Operações",
    bg: "bg-[#d8f5e8]",
    border: "border-emerald-200/80",
  },
  {
    quote:
      "Antes tudo era planilha e direct do Instagram perdido. Agora entra no CRM com histórico organizado. O agente cobre o fora do expediente e a gente entra só quando a conversa pede.",
    name: "Ricardo Mendes",
    role: "Head de Marketing",
    bg: "bg-[#e8e4f7]",
    border: "border-violet-200/80",
  },
  {
    quote:
      "Testamos outras ferramentas antes. Aqui dá para ajustar o tom do agente e as regras sem ficar dependendo de desenvolvedor. Para um time pequeno isso fez uma diferença enorme no resultado.",
    name: "Camila Rocha",
    role: "CEO e Fundadora",
    bg: "bg-[#fde8d8]",
    border: "border-orange-200/80",
  },
  {
    quote:
      "O acompanhamento automático evitou vários esquecimentos. Hoje sabemos quem respondeu, quem pediu orçamento e quem precisa de um empurrão, tudo visível no painel.",
    name: "Ana Paula Ferreira",
    role: "Gerente Comercial",
    bg: "bg-[#e0f2fe]",
    border: "border-sky-200/80",
  },
  {
    quote:
      "Conseguimos baratear o primeiro atendimento sem parecer robótico. O agente entende o contexto e quando o assunto fica sensível a conversa vai para um humano na hora certa.",
    name: "Diego Nunes",
    role: "COO",
    bg: "bg-[#f3e8ff]",
    border: "border-purple-200/80",
  },
  {
    quote:
      "Muita gente acha que está falando com alguém da equipe. Ajustar o tom e a velocidade das respostas subiu a nota nas pesquisas depois do atendimento.",
    name: "Fernanda Lima",
    role: "Líder de Experiência do Cliente",
    bg: "bg-[#ecfccb]",
    border: "border-lime-200/80",
  },
  {
    quote:
      "No consultório a agenda e a confirmação eram um gargalo. Com o fluxo integrado diminuíram as faltas e a recepção respirou para cuidar melhor de quem está na clínica.",
    name: "Gustavo Henrique",
    role: "Sócio-administrador",
    bg: "bg-[#ffedd5]",
    border: "border-amber-200/80",
  },
  {
    quote:
      "Canal e etapa do funil no mesmo lugar facilitou demais o dia a dia. Planejamos campanha e orçamento em cima do que realmente converte, não de suposição.",
    name: "Juliana Prado",
    role: "Diretora de Growth",
    bg: "bg-[#fce7f3]",
    border: "border-pink-200/80",
  },
] as const;

function ReviewCard({ item }: { item: (typeof testimonials)[number] }) {
  return (
    <figure
      className={cn(
        "relative flex h-full min-h-[200px] w-72 shrink-0 cursor-default flex-col overflow-hidden rounded-xl border p-4 shadow-sm transition-shadow hover:shadow-md",
        item.bg,
        item.border,
      )}
    >
      <div className="min-w-0 text-left">
        <figcaption className="text-sm font-semibold leading-snug text-slate-900">{item.name}</figcaption>
        <p className="mt-0.5 text-xs font-medium text-slate-600">{item.role}</p>
      </div>
      <blockquote className="mt-3 flex-1 text-left text-sm leading-relaxed text-slate-800">{item.quote}</blockquote>
    </figure>
  );
}

const midpoint = Math.ceil(testimonials.length / 2);
const firstRow = testimonials.slice(0, midpoint);
const secondRow = testimonials.slice(midpoint);

export const Testimonials = () => {
  const { ref, isInView } = useInView();

  return (
    <section
      id="depoimentos"
      className="scroll-mt-24 border-t border-slate-200/80 bg-[#f8fafc] py-20 md:py-24"
      aria-labelledby="testimonials-heading"
    >
      <div className="container mx-auto px-4">
        <div
          ref={ref}
          className={cn(
            "mx-auto mb-10 max-w-3xl text-center transition-all duration-700 md:mb-14",
            isInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <p className="mb-3 text-sm font-medium text-primary">
            Depoimentos de quem usa FabChat
          </p>
          <h2
            id="testimonials-heading"
            className="text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl md:text-5xl text-balance"
          >
            Quem centralizou WhatsApp, Instagram e CRM no mesmo fluxo
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
            Relatos de empresas que usam agentes de IA para responder mais rápido, organizar conversas e
            acompanhar oportunidades com histórico completo. Quer o mesmo para o seu time?{" "}
            <Link to="#pricing" className="font-medium text-primary underline-offset-4 hover:underline">
              Veja os planos
            </Link>{" "}
            e comece em poucos minutos.
          </p>
        </div>

        <div className="relative mx-auto flex w-full max-w-6xl flex-col items-center justify-center overflow-hidden">
          <Marquee pauseOnHover className="[--duration:55s] [--gap:1rem]">
            {firstRow.map((item) => (
              <ReviewCard key={item.name} item={item} />
            ))}
          </Marquee>
          <Marquee reverse pauseOnHover className="[--duration:55s] [--gap:1rem]">
            {secondRow.map((item) => (
              <ReviewCard key={item.name} item={item} />
            ))}
          </Marquee>

          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-1/4 bg-gradient-to-r from-[#f8fafc] to-transparent"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-1/4 bg-gradient-to-l from-[#f8fafc] to-transparent"
            aria-hidden
          />
        </div>
      </div>
    </section>
  );
};
